import {calcUserScore, filterObjectByParams} from 'helpers/common-helper';
import Player from './player';
import Const from 'constants';
import Action from 'actions';
import {calcSympathyPoints, getDefaultExposure} from 'helpers/player-helper';
import {PlayersPoolBuilder} from 'services/players-pool/players-pool-builder';
import NumberHelper, {toFloat} from 'helpers/number-helper';
import {getPlayerModeProjOwnFunctionality} from 'helpers/projected-ownership';

export default class PlayerSinglegame extends Player {
    modes = [Const.CAPTAIN_MODE, Const.FLEX_MODE];

    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        if (!filterPlayersBeforePreparingPools) {
            return this.exec('createPoolsOfPlayers', {
                players: Object.values(players),
            });
        }

        const {
            playersData: {filters},
        } = Action.getState();

        const params = {
            team: {
                filterType: Const.FILTER_TYPE_IN_ARRAY,
                filterValue: filters.games,
            },
        };

        players = filterObjectByParams(players, params, true);

        return this.exec('createPoolsOfPlayers', {
            players,
        });
    }

    resetPoints({incomingValues: {dispatch}}) {
        return this.exec('resetPoints_ShowDown', {dispatch, modes: this.modes});
    }

    resetSettings({incomingValues: {dispatch}}) {
        return this.exec('resetSettings_ShowDown', {dispatch, modes: this.modes});
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        const {playersLock} = Action.getState();

        const builder = new PlayersPoolBuilder(players);

        builder.addTaskOnScenario('position');
        builder.addTaskOnScenario('playerId');
        builder.addTaskOnScenario('teamAndPosition');
        builder.addTaskOnProperty('team');
        builder.addTaskOnProperty('exposureMin', (player) => {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMin > 0
            );
        });
        builder.addTaskOnProperty('exposureMax', (player) => {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMax < 100
            );
        });
        builder.addTaskOnProperty('captain_exposureMin', (player) => {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMin > 0
            );
        });
        builder.addTaskOnProperty('captain_exposureMax', (player) => {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMax < 100
            );
        });
        builder.addTaskOnProperty('overall_exposureMax', (player) => {
            return !playersLock.isLockedAnywhere(player.getId()) && player.overall_exposureMax < 100;
        });

        return builder.execTasks();
    }

    getExpandedEntityParams({incomingValues = {}}) {
        const {modesConfig} = Action.getState();
        const {userPointsType = 'adminPoints'} = incomingValues;
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const userPoints = parseFloat(expandedPlayer[userPointsType]);
        const cptPointCoef = modesConfig.getConfig('CPT').getPointCoefficient();
        const captainSalary = expandedPlayer.salary * cptPointCoef;
        const captainUserPoints = userPoints * cptPointCoef;
        const captainAdminPoints = expandedPlayer.adminPoints * cptPointCoef;
        const captainPoints = expandedPlayer.points * cptPointCoef;
        const captainGrades = expandedPlayer.grades * cptPointCoef;
        const captainFantasyPoints = expandedPlayer.fantasyPoints * cptPointCoef;
        const PlayerService = this;

        return {
            ...expandedPlayer,
            grades: parseFloat(expandedPlayer.grades),
            homeRunPrice: parseFloat(expandedPlayer.homeRunPrice ?? 0),
            [Const.EXCLUDED_BY_ORDER_RANGE]: false,
            [Const.EXCLUDED_BY_CONFIRMED_RANGE]: false,
            overall_exposureMax: getDefaultExposure('max'),
            expected_minutes: parseInt(expandedPlayer.expected_minutes),
            points_per_minute: parseFloat(expandedPlayer.points_per_minute),
            userPoints: userPoints,
            userScore: calcUserScore(userPoints, expandedPlayer.salary, this.userScoreMultiplier),
            lineupRole: Const.DEFAULT_MODE,
            captain_salary: captainSalary,
            captain_fantasyPoints: toFloat(captainFantasyPoints, 3),
            captain_points: toFloat(captainPoints, 3),
            captain_adminPoints: toFloat(captainAdminPoints, 3),
            captain_userPoints: toFloat(captainUserPoints, 3),
            captain_grades: toFloat(captainGrades, 3),
            captain_userScore: calcUserScore(captainUserPoints, expandedPlayer.salary, this.userScoreMultiplier),
            captain_exposureMax: getDefaultExposure('max'),
            captain_exposureMin: getDefaultExposure('min'),
            captain_pointsChangedManually: false,
            projOwn: {...getPlayerModeProjOwnFunctionality(expandedPlayer, ['cpt', 'flex'])},
            isConfirmed() {
                return expandedPlayer.confirmed === Const.MLB_PLAYER_CONFIRMED;
            },
            isPitcher() {
                return this.positionName === 'P';
            },
            getCapPropValue(prop) {
                if (prop === 'projected_ownership') {
                    return this.projOwn.getValue(Const.CAPTAIN_MODE);
                }

                let value,
                    propName = this.getCapPropName(prop);
                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }
                return value;
            },
            isExcludedByFilter() {
                return this[Const.EXCLUDED_BY_CONFIRMED_RANGE] || this[Const.EXCLUDED_BY_ORDER_RANGE];
            },
            include() {
                this.includeBy([Const.EXCLUDED_BY_CONFIRMED_RANGE, Const.EXCLUDED_BY_ORDER_RANGE]);
            },
            resetExposure() {
                this.overall_exposureMax = getDefaultExposure('max');
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
                this.captain_exposureMin = getDefaultExposure('min');
                this.captain_exposureMax = getDefaultExposure('max');
            },
            getPropNameOfMode(prop, incomingMode) {
                let mode = incomingMode || this.mode;
                return PlayerService.exec('definePropNameOfMode', {prop: prop, mode: mode});
            },

            getPropNameOfLineupRole(prop) {
                let propName = prop;

                if (this.getIsCaptain()) {
                    propName = this.getCapPropName(prop);
                }

                return propName;
            },
            getPropValueOfLineupRole(prop) {
                if (prop === 'projected_ownership') {
                    let mode = Const.DEFAULT_MODE;
                    if (this.getIsCaptain()) {
                        mode = Const.CAPTAIN_MODE;
                    }
                    return this.projOwn.getValue(mode);
                }

                let value,
                    propName = this.getPropNameOfLineupRole(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getLineupRole() {
                return this.lineupRole;
            },
            getCapPropName(prop) {
                let mode = this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE;
                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getIsCaptain() {
                return this.lineupRole === Const.CAPTAIN_MODE;
            },
            setAsCaptain() {
                this.lineupRole = Const.CAPTAIN_MODE;
            },
            setAsFlex() {
                this.lineupRole = Const.DEFAULT_MODE;
            },
            isFitForLineup(substitutablePlayer) {
                return true;
            },
        };
    }

    definePropNameOfMode({incomingValues: {prop, mode}}) {
        const extraProps = [
            'exposureMax',
            'exposureMin',
            'salary',
            'points',
            'adminPoints',
            'userPoints',
            'userScore',
            'grades',
            'fantasyPoints',
            'pointsChangedManually',
        ];
        const overallPropsList = ['exposureMax'];

        if (Const.CAPTAIN_MODE === mode && inArray(prop, extraProps)) {
            return 'captain_' + prop;
        } else if (Const.OVERALL_MODE === mode && inArray(prop, overallPropsList)) {
            return 'overall_' + prop;
        }

        return prop;
    }

    loadExtraPlayerDataFromStore({incomingValues: {dispatch, data}}) {
        if (data.hasOwnProperty('rangeOfOutcomesByPositions')) {
            dispatch(Action.setRangeOfOutcomesByPositionsFromStorage(data.rangeOfOutcomesByPositions));
        }

        if (data.hasOwnProperty('captain_userPoints')) {
            let captain_pointsChangedManually = false;

            if (typeof data['captain_pointsChangedManually'] !== 'undefined') {
                captain_pointsChangedManually = data['captain_pointsChangedManually'];
            }

            dispatch(
                Action.set('UserPoints', data.captain_userPoints, Const.CAPTAIN_MODE, captain_pointsChangedManually),
            );
        }

        if (data.hasOwnProperty('captain_userExposure')) {
            dispatch(Action.set('PlayersExposure', data.captain_userExposure));
        }

        if (data.hasOwnProperty('playerConfirmed')) {
            dispatch(Action.set('PlayerConfirmedFilterFromStorage', data.playerConfirmed));
        }

        if (data.hasOwnProperty('hittersOrder')) {
            dispatch(Action.set('HittersOrderFromStorage', data.hittersOrder));
        }
    }

    getExtraPlayerDataToStore({...rest}) {
        const {players} = Action.getState('playersData', {});

        if (!players.hasPlayers()) {
            return {};
        }

        const capUserPoints = [];
        const capUserExposure = [];
        const captain_pointsChangedManually = [];

        players.getAllAsArr().map((player) => {
            if (player.captain_adminPoints !== player.captain_userPoints) {
                capUserPoints.push({
                    playerId: player.playerId,
                    userPoints: player.captain_userPoints,
                    mode: Const.CAPTAIN_MODE,
                });
            }

            if (player.captain_exposureMax !== getDefaultExposure('max')) {
                capUserExposure.push({
                    playerId: player.playerId,
                    exposureMax: player.captain_exposureMax,
                    mode: Const.CAPTAIN_MODE,
                });
            }

            if (player.captain_exposureMin !== getDefaultExposure('min')) {
                capUserExposure.push({
                    playerId: player.playerId,
                    exposureMin: player.captain_exposureMin,
                    mode: Const.CAPTAIN_MODE,
                });
            }

            if (player.captain_pointsChangedManually) {
                captain_pointsChangedManually.push(player.playerId);
            }
        });

        return {
            captain_userPoints: capUserPoints,
            captain_userExposure: capUserExposure,
            captain_pointsChangedManually,
        };
    }

    updatePlayersAfterRefreshingProjections({incomingValues: {indexedPlayers}}) {
        const {
            playersData: {players, games},
            playersSympathy,
        } = Action.getState();

        const result = {};

        Object.keys(indexedPlayers).map((key) => {
            const newPlayer = indexedPlayers[key];
            const player = {...players.getPlayer(newPlayer.getId())};

            if (isEmpty(player)) {
                result[key] = {...newPlayer};
                return;
            }

            const isGameActive = !games.isPlayerGameInactive(player);
            let userPoints = player.userPoints;
            let userScore = player.userScore;
            let captain_userPoints = player.captain_userPoints;
            let captain_userScore = player.captain_userScore;

            if (!player.pointsChangedManually && isGameActive) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, newPlayer.salary, newPlayer.userScoreMultiplier);
            }

            if (!player.captain_pointsChangedManually && isGameActive) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId(), Const.CAPTAIN_MODE);
                captain_userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.captain_userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                captain_userScore = calcUserScore(captain_userPoints, newPlayer.salary, newPlayer.userScoreMultiplier);
            }

            const {projOwn = {}, ...newPlayerProps} = newPlayer;

            player.projOwn.setValueFromPlayerPayload(newPlayer);

            result[key] = {
                ...player,
                ...newPlayerProps,
                pointsChangedManually: player.pointsChangedManually,
                userPoints,
                userScore,
                exposureMax: player.exposureMax,
                exposureMin: player.exposureMin,
                captain_pointsChangedManually: player.captain_pointsChangedManually,
                captain_userPoints,
                captain_userScore,
                captain_exposureMax: player.captain_exposureMax,
                captain_exposureMin: player.captain_exposureMin,
                overall_exposureMax: player.overall_exposureMax,
            };
        });

        return {...result};
    }

    applySympathy({incomingValues: {players, playersSympathy}}) {
        const result = {};
        const indexedPlayers = players.all();

        Object.keys(indexedPlayers).map((key) => {
            const player = indexedPlayers[key];
            let userPoints = player.userPoints;
            let userScore = player.userScore;
            let captain_userPoints = player.captain_userPoints;
            let captain_userScore = player.captain_userScore;

            if (!player.pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(player.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, player.salary, player.userScoreMultiplier);
            }

            if (!player.captain_pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId(), Const.CAPTAIN_MODE);
                captain_userPoints = NumberHelper.float(
                    calcSympathyPoints(player.captain_userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                captain_userScore = calcUserScore(captain_userPoints, player.salary, player.userScoreMultiplier);
            }

            result[key] = {
                ...player,
                userPoints,
                userScore,
                captain_userPoints,
                captain_userScore,
            };
        });

        return {...result};
    }

    preparePlayerForLineup({incomingValues = {}}) {
        return this.exec('preparePlayerForLineup_ShowDown', incomingValues);
    }

    prepareRandomPoints({incomingValues = {}}) {
        return this.exec('prepareRandomPointsByModes', {
            modes: this.modes,
        });
    }

    updatePlayersWhoseGamesBecameInactive({incomingValues: {dispatch, players}}) {
        return this.exec('updatePlayersWhoseGamesBecameInactive_ShowDown', {
            dispatch,
            players,
            modes: this.modes,
        });
    }

    updatePlayersWhoseGamesBecameActive({incomingValues: {dispatch, players}}) {
        return this.exec('updatePlayersWhoseGamesBecameActive_ShowDown', {
            dispatch,
            players,
            modes: this.modes,
        });
    }
}
